'use client'

import React from 'react'
import Link from 'next/link'
import Image from 'next/image'
import { getBaseImageUrl, getMerchantHref } from '@/constants/hooks'
import { Store, ArrowUpRight } from 'lucide-react'

interface Props {
    merchants: any[]
    title?: string
    subtitle?: string
    domain: string
    storeSlug?: string
    slugType?: string
}

const PromotionBrandsGrid: React.FC<Props> = ({
    merchants,
    title = 'Featured Partner Stores',
    subtitle = 'Popular retailers offering special discounts in this campaign',
    domain,
    storeSlug = 'store',
    slugType = 'slug',
}) => {
    if (!merchants || merchants.length === 0) return null

    return (
        <div className="w-full relative">
            {/* Header with Title and Badges */}
            <div className="flex flex-wrap items-center justify-between gap-3 mb-6 pb-3 border-b border-slate-200/80">
                <div className="flex items-center gap-3">
                    <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-2xl bg-indigo-50 border border-indigo-100 text-indigo-600 flex items-center justify-center shrink-0 shadow-2xs">
                        <Store size={18} />
                    </div>
                    <div>
                        <div className="flex items-center gap-2">
                            <h4 className="text-base sm:text-2xl font-black text-slate-900 tracking-tight leading-snug">
                                {title}
                            </h4>
                        </div>
                        <p className="text-xs sm:text-sm text-slate-500 font-medium mt-1">
                            {subtitle}
                        </p>
                    </div>
                </div>
            </div>

            {/* Circular Avatar Cards Grid */}
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-6 xl:grid-cols-8 gap-y-6 gap-x-3 sm:gap-x-4">
                {merchants.map((merchant, i) => {
                    const href = getMerchantHref(merchant, storeSlug || 'store', slugType || 'slug')
                    const name = merchant?.merchant_name || 'Brand'
                    const logo = merchant?.merchant_logo

                    return (
                        <Link
                            key={merchant?.id || i}
                            href={href}
                            className="group flex flex-col items-center text-center transition-all duration-300"
                        >
                            {/* Circular Glow Wrapper */}
                            <div className="relative w-16 h-16 sm:w-20 sm:h-20 rounded-full p-[1.5px] bg-gradient-to-tr from-slate-200 via-slate-100 to-indigo-100 group-hover:from-indigo-500 group-hover:via-purple-500 group-hover:to-pink-500 shadow-2xs group-hover:shadow-[0_0_18px_rgba(99,102,241,0.35)] group-hover:scale-105 transition-all duration-300 flex items-center justify-center">
                                {/* Inner White Circle */}
                                <div className="w-full h-full rounded-full bg-white flex items-center justify-center p-2.5 relative overflow-hidden">
                                    {logo ? (
                                        <Image
                                            src={getBaseImageUrl(domain, logo, '')}
                                            alt={name}
                                            fill
                                            className="object-contain p-2"
                                            unoptimized={true}
                                        />
                                    ) : (
                                        <span className="text-indigo-600 font-black text-lg sm:text-xl">
                                            {name?.charAt(0)?.toUpperCase() || 'B'}
                                        </span>
                                    )}
                                </div>

                                {/* Floating Tiny Action Pill on Hover */}
                                <div className="absolute -bottom-0.5 -right-0.5 w-5 h-5 sm:w-5.5 sm:h-5.5 rounded-full bg-slate-800 text-white group-hover:bg-indigo-600 border-[1.5px] border-white flex items-center justify-center shadow-xs transition-colors duration-200 group-hover:scale-110">
                                    <ArrowUpRight size={10} className="text-white" />
                                </div>
                            </div>

                            {/* Brand Name */}
                            <p className="text-xs sm:text-[13px] font-bold text-slate-800 group-hover:text-indigo-600 transition-colors mt-2.5 truncate w-full max-w-[85px] sm:max-w-[105px]">
                                {name}
                            </p>

                            {/* Action Subtext */}
                            <span className="text-[10px] font-semibold text-slate-400 group-hover:text-indigo-600 transition-colors tracking-tight">
                                Shop Now
                            </span>
                        </Link>
                    )
                })}
            </div>
        </div>
    )
}

export default PromotionBrandsGrid
